<template>
  <div class="select-wrapper relative">
    <select
      v-if="!isSearchable"
      class="pointer"
      :name="name"
      :class="{
        'cl-tertiary' : options.length === 0,
        'empty': !selected
      }"
      :autocomplete="autocomplete"
      :disabled="disabled"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
      @change="$emit('change', $event.target.value)"
      @input="$emit('input', $event.target.value)"
    >
      <option disabled selected value v-if="!selected" />
      <option
        v-for="(option, key) in options"
        :key="key"
        :value="option.value"
        v-bind="{selected: option.value === selected}"
      >
        {{ $t(option.label) }}
      </option>
    </select>
    <template v-else>
      <div v-show="dropdownActive" class="click-outside-overlay" @click="closeDropdown()" />
      <div class="searchable-select">
        <div
          class="searchable-select__value pointer"
          :class="{ active: dropdownActive, empty: !selectedLabel, disabled: disabled }"
          @click="toggleDropdown()"
        >
          {{ selectedLabel }}
        </div>
        <div v-show="dropdownActive" class="searchable-select__dropdown">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="searchable-select__search"
            :placeholder="$t('Search')"
            @click.stop
          >
          <div class="searchable-select__options">
            <div v-if="!filteredOptions.length" class="searchable-select__no-results">
              {{ $t('No results were found.') }}
            </div>
            <div
              v-for="(option, key) in filteredOptions"
              :key="key"
              class="searchable-select__option pointer"
              :class="{ active: option.value === selected }"
              @click="selectOption(option)"
            >
              {{ option.label }}
            </div>
          </div>
        </div>
      </div>
    </template>
    <label>{{ placeholder }}</label>
    <ValidationMessages v-if="validations" :validations="validations" />
  </div>
</template>

<script>
import ValidationMessages from './ValidationMessages.vue'

const SEARCH_THRESHOLD = 10

export default {
  name: 'BaseSelect',
  components: {
    ValidationMessages
  },
  props: {
    id: {
      type: String,
      required: false,
      default: ''
    },
    name: {
      type: String,
      required: false,
      default: ''
    },
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    selected: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    autocomplete: {
      type: String,
      required: false,
      default: ''
    },
    validations: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    searchable: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      dropdownActive: false,
      searchQuery: ''
    }
  },
  computed: {
    isSearchable () {
      return this.searchable && this.options.length > SEARCH_THRESHOLD
    },
    translatedOptions () {
      return this.options.map(option => ({ value: option.value, label: this.$t(option.label) }))
    },
    filteredOptions () {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return this.translatedOptions
      return this.translatedOptions.filter(option => String(option.label).toLowerCase().includes(query))
    },
    selectedLabel () {
      const option = this.translatedOptions.find(option => option.value === this.selected)
      return option ? option.label : ''
    }
  },
  methods: {
    toggleDropdown () {
      if (this.disabled) return
      this.dropdownActive ? this.closeDropdown() : this.openDropdown()
    },
    openDropdown () {
      this.dropdownActive = true
      this.searchQuery = ''
      this.$emit('focus')
      this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus())
    },
    closeDropdown () {
      this.dropdownActive = false
      this.searchQuery = ''
      this.$emit('blur')
    },
    selectOption (option) {
      this.$emit('input', option.value)
      this.$emit('change', option.value)
      this.closeDropdown()
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~theme/css/variables/colors';
  @import '~theme/css/helpers/functions/color';
  @import '~theme/css/base/text';
  $secondary-gray: color(secondary-gray);
  $gray-theme: color(gray-theme);
  $main-smoke: color(main-smoke);
  $select-text: #002138;
  $select-selected-weight: 600;

select {
  font-family: 'brandon-grotesque';
}

.select-wrapper {
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 45%;
    right: 15px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 8px 6px 0 6px;
    border-color: #707070 transparent transparent transparent;
    pointer-events: none;
    @media (max-width: 767px) {
      top: 50%;
    }
  }

  select {
    position: relative;
    border: none;
    width: 100%;
    -moz-appearance: none;
    -webkit-appearance: none;
    font-family: 'Poppins';
    line-height: 20px;
    font-size: 0.8em;
    color: $select-text;
    padding: 10px;
    border-radius: 0;
    border: 1px solid #D6D8DE;
    &:hover,
    &:focus {
      outline: none;
      border-color: #000000;
    }

    &:disabled,
    &:disabled + label {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
  .searchable-select {
    position: relative;
    width: 100%;

    &__value {
      border: 1px solid #D6D8DE;
      background-color: white;
      font-family: 'Poppins';
      line-height: 20px;
      font-size: 0.8em;
      color: $select-text;
      font-weight: $select-selected-weight;
      padding: 10px;
      min-height: 20px;
      padding-right: 30px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      &.empty {
        font-weight: 400;
      }
      &:hover,
      &.active {
        border-color: #000000;
      }
      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }
    }

    &__dropdown {
      position: absolute;
      z-index: 5;
      top: calc(100% - 1px);
      left: 0;
      right: 0;
      background-color: white;
      border: 1px solid #D6D8DE;
      border-top: 0;
      padding: 10px;
    }

    &__search {
      width: 100%;
      padding: 6px 8px;
      border: 1px solid #D6D8DE;
      background-color: white;
      font-family: inherit;
      font-size: 13px;
      color: $select-text;
      box-sizing: border-box;
      &:focus {
        outline: none;
        border-color: black;
      }
    }

    &__options {
      max-height: 150px;
      overflow-y: auto;
      margin-top: 10px;
    }

    &__option {
      padding: 5px 0;
      color: $select-text;
      font-weight: 400;
      font-size: 13px;
      &:hover,
      &.active {
        font-weight: $select-selected-weight;
      }
    }

    &__no-results {
      padding: 5px 0;
      color: #707070;
      font-weight: 400;
      font-size: 13px;
    }
  }

  .click-outside-overlay {
    position: fixed;
    z-index: 4;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  label {
    position: absolute;
    background-color: #fff;
    color:#707070;
    top: -7px;
    left: 20px;
    padding-left: 5px;
    padding-right: 5px;
    font-size: 0.8em;
    pointer-events:none;
    user-select: none;
  }
}
</style>
